import React, { useState } from "react"
import { Box, Flex, Container, Button, Image, Heading, Text, FormControl, FormLabel, InputGroup, Input, VStack } from "@chakra-ui/react"
import Link from "@app/components/Link"
import { useConfigContext } from "@app/providers/config"
import { useFunctions } from "@app/hooks/useFunctions"
import { useShopify } from "@app/hooks/useShopify"
import { useGiftcard } from "@app/hooks/useGiftcard"
import GC from "@static/images/gc.png"
import Peace from "@static/images/peace.png"
import Rainbow from "@static/images/rainbow.png"
import type { Location } from "@root/types/global"

type Props = {
  location: Location
}

const ProductGiftcardQuickBalance: React.FC<Props> = ({ location }) => {
  const { giftcardSettings } = useGiftcard(location)
  const { formatDate, formatMoney } = useShopify()
  const { callFunction } = useFunctions()
  const {
    settings: { routes },
  } = useConfigContext()
  const [showBalance, setShowBalance] = useState(false)
  const [voucher, setVoucher] = useState("")
  const [pin, setPin] = useState("")
  const [voucherError, setVoucherError] = useState(false)
  const [pinError, setPinError] = useState(false)
  const [balanceloading, setBalanceLoading] = useState(false)
  const [data, setData] = useState<any>(null)
  const [notFound, setNotFound] = useState(false)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setVoucherError(false)
    setPinError(false)
    setData(null)
    setNotFound(false)

    let error = false
    if (voucher?.length === 0) {
      setVoucherError(true)
      error = true
    }
    if (pin?.length === 0) {
      setPinError(true)
      error = true
    }

    if (error) {
      return
    }

    setBalanceLoading(true)

    const res = await callFunction("gift-card-balance", { voucher, pin, history: false })

    setBalanceLoading(false)
    if (!res) {
      setNotFound(true)
      return
    }

    if (res.status === "error" && res.body === "Not found") {
      setNotFound(true)
    } else if (res.status === "success") {
      setData(res.body)
      setShowBalance(true)
    }
  }

  const onReset = () => {
    setVoucherError(false)
    setPinError(false)
    setData(null)
    setNotFound(false)
    setShowBalance(false)
  }

  return (
    <Box mt="5">
      <Box width="100%" position="relative" mb="20">
        <ConcaveRectangle fill="#FFAE61" />

        <Image src={GC} position="absolute" right="0" display={{ base: "none", md: "block" }} width={{ md: "112.5", lg: "auto" }} />
        <Box background="#FFAE61" py="25" my="-0.313rem">
          <Container maxWidth="137.5">
            <Flex direction="column" alignItems="center">
              <Image
                src={Rainbow}
                position="absolute"
                left="6.25"
                top="16.25"
                display={{ base: "none", md: "block" }}
                width={{ md: "50", lg: "auto" }}
              />
              <Image
                src={Peace}
                position="absolute"
                bottom={{ base: "unset:", md: "37.5" }}
                top={{ base: "-40px", md: "unset" }}
                left={{ base: "-5px", md: "21.25" }}
                right={{ base: "0", md: "unset" }}
                width={{ base: "37.5", md: "43.75", lg: "auto" }}
                margin={{ base: "auto", md: "inherit" }}
                transform={{ base: "rotate(-30deg)", md: "rotate(0)" }}
              />

              {giftcardSettings?.balanceTitle && (
                <Heading
                  variant="h3"
                  fontSize={{ base: "2xl", md: "5xl" }}
                  lineHeight={{ base: "7", md: "14" }}
                  mb="4"
                  fontWeight={700}
                  textAlign="center"
                  px={{ base: "0", md: "12.5" }}
                >
                  {giftcardSettings.balanceTitle}
                </Heading>
              )}
              {giftcardSettings?.balanceSubTitle && (
                <Text variant="text16" mb="6">
                  {giftcardSettings.balanceSubTitle}
                </Text>
              )}

              {showBalance ? (
                <>
                  <Box background="#ffefdf" padding="5" width={{ base: "100%", sm: "107.5" }}>
                    <VStack spacing="3" alignItems="left">
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text variant="text18" fontWeight={700}>
                          Current Balance:
                        </Text>
                        <Text>{formatMoney(data.balance.available_amount)}</Text>
                      </Flex>
                      {data.balance.expiry_date && <Box>Expires: {formatDate(data.balance.expiry_date)}</Box>}

                      <Link
                        to={routes.GIFTCARD}
                        state={{
                          voucher,
                          pin,
                        }}
                        sx={{
                          textDecoration: "underline",
                        }}
                      >
                        View full balance history here
                      </Link>
                    </VStack>
                  </Box>
                  <Button variant="fill" fontSize="md" mt="6" width="100%" onClick={() => onReset()}>
                    Reset
                  </Button>
                </>
              ) : (
                <Box as="form" onSubmit={onSubmit} width={{ base: "100%", sm: "107.5" }}>
                  <InputGroup alignItems="center" pos="relative" flexDirection="column" mb="6">
                    <FormControl id="gcNumber" mb="4">
                      {giftcardSettings?.balanceInputLabel && (
                        <FormLabel lineHeight="5" fontSize="xs" mb="1">
                          {giftcardSettings?.balanceInputLabel}
                        </FormLabel>
                      )}

                      <Input
                        name="gcNumber"
                        variant="giftCardAlt"
                        type="number"
                        placeholder="Giftcard Number"
                        onChange={e => setVoucher(e.target.value)}
                      />
                      {voucherError && (
                        <Text variant="text12" fontWeight={600} mt={3} mb={0}>
                          Please enter a card number.
                        </Text>
                      )}
                    </FormControl>
                    <FormControl id="gcPin">
                      {giftcardSettings?.balancePinLabel && (
                        <FormLabel lineHeight="5" fontSize="xs" mb="1">
                          {giftcardSettings.balancePinLabel}
                        </FormLabel>
                      )}

                      <Input
                        name="gcPin"
                        variant="giftCardAlt"
                        type="number"
                        placeholder="Giftcard Pin"
                        onChange={e => setPin(e.target.value)}
                      />
                      {pinError && (
                        <Text variant="text12" fontWeight={600} mt={3} mb={0}>
                          Please enter a pin.
                        </Text>
                      )}
                      {notFound && (
                        <Text variant="text12" mt={10} fontWeight={600} textAlign="center" mb={0}>
                          We could not find that card number.
                          <br />
                          Try re-entering your number or PIN.
                        </Text>
                      )}
                    </FormControl>
                  </InputGroup>

                  <Button variant="fill" fontSize="md" width="100%" type="submit" isLoading={balanceloading}>
                    {giftcardSettings?.balanceSubmitLabel || "Submit"}
                  </Button>
                </Box>
              )}
            </Flex>
          </Container>
        </Box>

        <Box transform="scaleY(-1)">
          <ConcaveRectangle fill="#FFAE61" />
        </Box>
      </Box>
    </Box>
  )
}

const ConcaveRectangle = ({ fill = "currentColor" }) => (
  <svg width="100%" viewBox="0 0 1440 105" xmlns="http://www.w3.org/2000/svg">
    <path fill={fill} d="M0 0C193.829 50.051 445.258 80.266 720 80.266C994.742 80.266 1246.17 50.051 1440 0V105H0V0Z"></path>
  </svg>
)

export default ProductGiftcardQuickBalance
